import { DateType } from '../constants/dates';
import { computeLocationInfo } from '../pages/search/utils/tracking';
import {
    OrganicEventSearchApiPayload,
    OrganicEventSearchApiResponse,
    searchOrganicEvents,
} from './searchOrganicEvents';
import { searchPromotedEvents } from './searchPromotedEvents';

type AllEventsSearchPayload = OrganicEventSearchApiPayload & {
    /**
     * Name of the frontend surface that is requesting the promoted events.
     */
    interfaceName: string;
    /**
     * Slots: number of promoted events to retrieve from backend.
     */
    slots: number;
};

export async function searchAllEvents(
    search: AllEventsSearchPayload,
): Promise<OrganicEventSearchApiResponse> {
    // eslint-disable-next-line
    const { interfaceName, slots, ...organicSearch } = search;
    const [organicEventsSearchResponse, promotedEventsSearchResponse] =
        await Promise.allSettled([
            searchOrganicEvents(organicSearch),
            searchPromotedEvents({
                ...transfromToPromotedEventSearchApiPayload(search),
            }),
        ]);

    if (organicEventsSearchResponse.status === 'rejected') {
        throw new Error(organicEventsSearchResponse.reason);
    }

    if (promotedEventsSearchResponse.status === 'fulfilled') {
        organicEventsSearchResponse.value.events.promoted_results =
            promotedEventsSearchResponse.value;
    }

    return organicEventsSearchResponse.value;
}

function transfromToPromotedEventSearchApiPayload(
    search: AllEventsSearchPayload,
) {
    const { event_search, slots, interfaceName } = search;
    const { latitude, longitude, place_id } = computeLocationInfo(event_search);

    return {
        slots,
        page: event_search.page || 1,
        app: 'web',
        interface: interfaceName,
        search: {
            dates: transformToDates(event_search.dates),
            date_range: event_search.date_range,
            client_timezone:
                event_search.client_timezone ||
                Intl.DateTimeFormat().resolvedOptions().timeZone,
            q: event_search.q,
            price: event_search.price,
            languages: event_search.languages,
            tags: event_search.tags,
            place: {
                is_online: event_search.online_events_only || false,
                place_id,
                geo_point:
                    longitude && latitude && !event_search.online_events_only
                        ? {
                              lon: Number(longitude),
                              lat: Number(latitude),
                              radius: 50,
                          }
                        : undefined,
            },
        },
    };
}

function transformToDates(dates?: DateType | DateType[]): DateType[] {
    if (!dates) {
        return ['current_future'];
    }
    // TODO: remove this workaround, here per retro-compatibility
    if (typeof dates === 'string') {
        return [dates];
    }
    return dates;
}
